import React, { useEffect, useState } from "react";
import { GiSpades } from "react-icons/gi";
import StorageIcon from "../components/StorageIcon";

// --- MUI IMPORTS ---
import {
  AppBar,
  Toolbar,
  IconButton,
  Box,
  Tooltip,
  useMediaQuery,
  Typography, // 💡 Import Typography for text styling
  Button, // 💡 Use MUI Button for the main action
  useTheme, // 💡 Use MUI's useTheme for consistent color access
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import MessageIcon from "@mui/icons-material/Message";
import MenuIcon from "@mui/icons-material/Menu";
import config from "../config";
import logo from "../assets/spade-logo.svg";

const baseUrl = config.baseURL;

// --- ENTERPRISE COLOR PALETTE SUGGESTIONS ---
// Using MUI's built-in theme access for better consistency
const EnterpriseColors = {
  AppBarBg: "#212B36", // Deep Slate/Navy for a common enterprise dark theme
  AppBarShadow: "0 4px 12px 0 rgba(0, 0, 0, 0.2)", // Subtle, pronounced shadow
  IconDefault: "#919EAB", // Muted Gray for secondary icons
  IconHover: "#D1D5DB", // Lighter Gray on hover
  PrimaryAction: "#EA6A12", // Your existing vibrant action color
  PrimaryActionHover: "#D65C0B",
  TextPrimary: "#FFFFFF",
  TextSecondary: "#A6ACAF",
};

const Navbar = ({ openDrawer }) => {
  const muiTheme = useTheme(); // Use MUI theme for standard access
  const [userData, setUserData] = useState(null);

  // Check for mobile size using the MUI theme breakpoint for consistency
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("md")); // Using 'md' (900px) is often better for a sidebar toggle

  // --- API Call remains the same ---
  useEffect(() => {
    const token = localStorage.getItem("token");

    if (!token) {
      console.log("No token found");
      return;
    }
    console.log("Using token:", token);
    const fetchUserData = async () => {
      try {
        const response = await fetch(`${baseUrl}/api/profile`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error("Error fetching user data:", errorText);
          throw new Error("Failed to fetch user data");
        }

        const contentType = response.headers.get("Content-Type");
        if (!contentType || !contentType.includes("application/json")) {
          console.error("Expected JSON, but got:", contentType);
          throw new Error("Response is not JSON");
        }

        const data = await response.json();
        setUserData(data.data);
        console.log("User data:", data);
      } catch (error) {
        console.error("Error:", error.message);
      }
    };

    fetchUserData();
  }, []);

  const handleAdvertiserClick = () => {
    window.location.href = "/dashboard/Advertiser";
  };

  // --- RENDERING ---
  return (
    <AppBar
      position="fixed"
      sx={{
        // Use standard enterprise dark color
        backgroundColor: EnterpriseColors.AppBarBg,
        // Standard, professional height
        height: "80px",
        // Enhanced shadow for enterprise feel
        boxShadow: EnterpriseColors.AppBarShadow,
        zIndex: (theme) => theme.zIndex.drawer + 1, // Ensure it's above the sidebar
      }}
    >
      <Toolbar
        // Ensure Toolbar takes full height and has proper padding
        sx={{
          minHeight: "64px !important",
          paddingLeft: { xs: "16px", md: "24px" }, // Standard padding
          paddingRight: { xs: "16px", md: "24px" },
          justifyContent: "space-between",
        }}
      >
        {/* Left Section (Hamburger Menu & Logo) */}
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {/* HAMBURGER MENU ICON (Mobile Only) */}
          {isMobile && (
            <IconButton
              edge="start"
              color="inherit"
              aria-label="menu"
              onClick={openDrawer}
              sx={{
                marginRight: 1, // MUI spacing unit (8px)
                color: EnterpriseColors.IconDefault,
                "&:hover": { color: EnterpriseColors.IconHover },
              }}
            >
              <MenuIcon />
            </IconButton>
          )}

          {/* Logo */}
          <Box
            component="img"
            src={logo}
            alt="Spade Logo"
            sx={{
              // Adjust size for a standard AppBar height (e.g., 40px)
              height: "98px",
              width: "auto",
              // margin: "px",
              padding: "2px",
            }}
          />
        </Box>

        {/* Right Section (Profile Info, Icons, and Button) */}
        <Box
          sx={{ display: "flex", alignItems: "center", gap: { xs: 1, md: 2 } }}
        >
          {/* Middle Section (Welcome Text) - Hide on mobile */}
          {!isMobile && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  color: EnterpriseColors.TextPrimary,
                  fontWeight: "bold",
                  lineHeight: 1.2,
                }}
              >
                Welcome, {userData?.companyName}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: EnterpriseColors.TextSecondary,
                  lineHeight: 1.2,
                  fontSize: "0.8rem", // Slightly smaller role text
                }}
              >
                {userData?.role}
              </Typography>
            </Box>
          )}

          {/* Icons Group */}
          {[MessageIcon, NotificationsIcon].map((Icon, index) => (
            <IconButton
              key={index}
              sx={{
                color: EnterpriseColors.IconDefault,
                "&:hover": {
                  color: EnterpriseColors.IconHover,
                  backgroundColor: "rgba(255, 255, 255, 0.08)",
                },
              }}
              aria-label={index === 0 ? "messages" : "notifications"}
            >
              <Icon fontSize="medium" />
            </IconButton>
          ))}

          {/* Storage Icon (assuming this is a custom avatar/profile component) */}
          <StorageIcon />

          {/* Advertiser Button (Conditionally render based on role) */}
          {/* {!isMobile && (
            <Button
              variant={
                userData?.role !== "advertiser" ? "contained" : "outlined"
              }
              onClick={handleAdvertiserClick}
              sx={{
                marginLeft: 2,
                minWidth: "180px", // Give the button a fixed, professional width
                borderRadius: "8px", // Standard enterprise button radius
                padding: "8px 16px",
                fontWeight: 600,
                fontSize: "0.9rem",

                // Contained (Become Advertiser)
                ...(userData?.role !== "advertiser" && {
                  backgroundColor: EnterpriseColors.PrimaryAction,
                  color: EnterpriseColors.TextPrimary,
                  "&:hover": {
                    backgroundColor: EnterpriseColors.PrimaryActionHover,
                  },
                }),

                // Outlined (Manage Ads)
                ...(userData?.role === "advertiser" && {
                  borderColor: EnterpriseColors.PrimaryAction,
                  color: EnterpriseColors.PrimaryAction,
                  "&:hover": {
                    borderColor: EnterpriseColors.PrimaryActionHover,
                    color: EnterpriseColors.PrimaryActionHover,
                    backgroundColor: "rgba(234, 106, 18, 0.04)", // Subtle background hover
                  },
                }),
              }}
              startIcon={<GiSpades size={20} />}
            >
              {userData?.role !== "advertiser"
                ? "Become an Advertiser"
                : "Manage Your Ads"}
            </Button>
          )} */}
          {!isMobile && (
            <Tooltip
              title="This feature is coming soon! Stay tuned."
              arrow
              placement="bottom"
            >
              <Box sx={{ display: "inline-block" }}>
                {" "}
                {/* Box wrapper helps tooltip work on disabled elements */}
                <Button
                  disabled // 1. Prevents interaction
                  variant="contained"
                  sx={{
                    marginLeft: 2,
                    minWidth: "180px",
                    borderRadius: "8px",
                    padding: "8px 16px",
                    fontWeight: 600,
                    fontSize: "0.9rem",

                    // 2. Greyed out "Locked" styling
                    backgroundColor: "#9CA3AF !important", // Force grey background
                    color: "#F3F4F6 !important", // Light grey text
                    cursor: "not-allowed",
                    boxShadow: "none",
                    position: "relative",
                    opacity: 0.8,

                    "&.Mui-disabled": {
                      backgroundColor: "#9CA3AF",
                      color: "#D1D5DB",
                    },
                  }}
                  startIcon={<GiSpades size={20} />}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    }}
                  >
                    {userData?.role !== "advertiser"
                      ? "Become an Advertiser"
                      : "Manage Your Ads"}

                    {/* 3. Small Badge inside the button */}
                    <span
                      style={{
                        fontSize: "10px",
                        opacity: 0.9,
                        textTransform: "uppercase",
                      }}
                    >
                      (Coming Soon)
                    </span>
                  </Box>
                </Button>
              </Box>
            </Tooltip>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;
